import { useCallback, useRef, useState } from "react"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { UploadIcon, Trash2Icon } from "lucide-react"
import { cn } from "@/lib/utils"
import { FileTypeIcon } from "./file-type-icon"
import { formatBytes, ACCEPTED_TYPES } from "./helpers"

type FileDropZoneProps = {
  files: File[]
  setFiles: React.Dispatch<React.SetStateAction<File[]>>
  disabled?: boolean
}

export function FileDropZone({
  files,
  setFiles,
  disabled = false,
}: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      if (disabled) return

      const arr = Array.from(newFiles)
      setFiles((prev) => {
        const existing = new Set(prev.map((f) => f.name + f.size))
        const unique = arr.filter((f) => !existing.has(f.name + f.size))
        return [...prev, ...unique]
      })
    },
    [setFiles, disabled]
  )

  const removeFile = useCallback(
    (index: number) => {
      setFiles((prev) => prev.filter((_, i) => i !== index))
    },
    [setFiles]
  )

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(true)
    },
    [disabled]
  )

  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
    },
    [disabled]
  )

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files)
      }
    },
    [addFiles, disabled]
  )

  return (
    <div
      className={cn(
        "space-y-2",
        disabled && "pointer-events-none opacity-50"
      )}
    >
      <Label>Attachments</Label>

      <div
        className={cn(
          "relative rounded-lg border-2 border-dashed transition-colors",
          disabled
            ? "cursor-not-allowed"
            : "cursor-pointer",
          isDragging && !disabled
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-muted-foreground/40"
        )}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => {
          if (!disabled) fileInputRef.current?.click()
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (disabled) return
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            fileInputRef.current?.click()
          }
        }}
        aria-disabled={disabled}
        aria-label="Upload files by clicking or dragging"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          disabled={disabled}
          className="hidden"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files)
            e.target.value = ""
          }}
        />

        <div className="flex flex-col items-center gap-2 py-8 px-4 text-center">
          <div className="flex items-center justify-center size-10 rounded-full bg-muted">
            <UploadIcon className="size-5 text-muted-foreground" />
          </div>

          <div>
            <p className="text-sm font-medium">
              {disabled
                ? "Close recipient picker to add files"
                : isDragging
                ? "Drop files here..."
                : "Click to upload or drag and drop"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              PDF, DOC, DOCX, PNG, JPG, TIFF
            </p>
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <div className="space-y-1.5 mt-2">
          {files.map((file, i) => (
            <div
              key={file.name + file.size}
              className="flex items-center gap-2.5 rounded-md border bg-muted/40 px-3 py-2 text-sm"
            >
              <FileTypeIcon name={file.name} />
              <span className="flex-1 truncate">{file.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">
                {formatBytes(file.size)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation()
                  removeFile(i)
                }}
              >
                <Trash2Icon className="size-3.5" />
                <span className="sr-only">Remove {file.name}</span>
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
